import { useEffect } from "react";
import { type FieldValues, useForm } from "react-hook-form";
import {
  useAdditionalInfoFormFields,
  type AdditionalInfoFormContext,
  type DriverLicenseFormContext,
  useDriverLicenseFormFields,
  useFrequentFlyerFormFields,
  type FrequentFlyerFormContext,
  usePassportFormFields,
  type PassengerFormContext,
  useTravellerDetailsFormFields,
  type TravellerDetailsFormContext,
  TravelerEditMode,
} from "@apac/travelers";
import type { Traveler } from "@b2bportal/traveler-api";
import {
  convertFormToTraveler,
  getTravellerFormValues,
} from "../utils/convertFormToTraveler";
export type TravellerFormValues = TravellerDetailsFormContext &
  PassengerFormContext &
  FrequentFlyerFormContext &
  AdditionalInfoFormContext &
  DriverLicenseFormContext;

export interface UseTravellerFormParams {
  traveler?: Traveler;
  dateFormat?: string;
  showPassportDetails?: boolean;
  showGender?: boolean;
  showNationality?: boolean;
  /**
   * Mark the gender field as required. Defaults to `showGender` for
   * backwards compatibility. If set to `true`, the field is also shown
   * (require implies show).
   */
  requireGender?: boolean;
  /**
   * Mark the nationality field as required. Defaults to `showNationality`
   * for backwards compatibility. If set to `true`, the field is also shown
   * (require implies show).
   */
  requireNationality?: boolean;
  /**
   * Mark the passport details section as required. Defaults to
   * `showPassportDetails` for backwards compatibility. If set to `true`,
   * the section is also shown (require implies show).
   */
  requirePassportDetails?: boolean;
  /**
   * When true (default), the nationality field is pre-filled with the
   * brand's preferred country code. Set false to leave it empty so the
   * user must make an explicit choice (HSBC-1316).
   */
  prefillNationality?: boolean;
  /** See `UseTravellerDetailsFormFieldsProps.enforceMinNameLength`. */
  enforceMinNameLength?: boolean;
  /** Advisory name errors shown when opening a saved traveler for correction. */
  initialNameErrors?: Partial<
    Record<"firstName" | "middleName" | "lastName", string>
  >;
  /**
   * When true (default), the driver license issuing country field is
   * pre-filled with the brand's preferred country code. Set false to
   * leave it empty so the user must make an explicit choice.
   */
  prefillIssuingCountry?: boolean;
  onSubmit: (traveller: Traveler) => void;
  travelerEditMode?: TravelerEditMode;
  showDriverLicenseDetails?: {
    showAddress: boolean;
    showCity: boolean;
  };
}

export interface UseTravellerFormResult {
  context: {
    formContext: ReturnType<typeof useForm>;
    frequentFlyerList: Array<FrequentFlyerFormContext>;
    existingPassenger: boolean;
  };
  handlers: {
    onFrequentFlyerSubmit: (data: FrequentFlyerFormContext) => void;
    onRemoveFrequentFlyer: (index: number) => void;
  };
}

export const useTravellerForm = ({
  traveler,
  onSubmit,
  dateFormat,
  showPassportDetails: showPassportDetailsProp,
  showGender: showGenderProp = true,
  showNationality: showNationalityProp = false,
  requireGender: requireGenderProp,
  requireNationality: requireNationalityProp,
  requirePassportDetails: requirePassportDetailsProp,
  prefillNationality,
  enforceMinNameLength = false,
  initialNameErrors,
  prefillIssuingCountry,
  travelerEditMode = TravelerEditMode.ALL,
  showDriverLicenseDetails = {
    showAddress: false,
    showCity: false,
  },
}: UseTravellerFormParams) => {
  // Resolve effective show/require for each conditional section. Two-step
  // merge:
  //   1. If a caller omits `requireXxx`, default it to the matching `showXxx`
  //      so existing callers see the same "rendered ⇒ required" behavior.
  //   2. "Require implies show" — a caller that only sets `requireXxx: true`
  //      automatically gets the section rendered. The resulting `showXxx`
  //      shadows the prop and is what we pass downstream.
  const requireGender = requireGenderProp ?? showGenderProp;
  const requireNationality = requireNationalityProp ?? showNationalityProp;
  const requirePassportDetails =
    requirePassportDetailsProp ?? !!showPassportDetailsProp;

  const showGender = showGenderProp || requireGender;
  const showNationality = showNationalityProp || requireNationality;
  const showPassportDetails =
    !!showPassportDetailsProp || requirePassportDetails;

  const { formFields: travelerFormFields, travellerDetailsDefaultValues } =
    useTravellerDetailsFormFields({
      dateFormat,
      showGender,
      travelerEditMode,
      showNationality,
      requireGender,
      requireNationality,
      prefillNationality,
      enforceMinNameLength,
    });

  const { formFields: passportFormFields, passportDetailsDefaultValues } =
    usePassportFormFields({
      dateFormat,
      traveler,
      requirePassportDetails,
      prefillNationality,
    });

  const {
    formFields: licenseFields,
    driverLicenseDetailsDefaultValues: licenseDefaultValues,
  } = useDriverLicenseFormFields({
    ...showDriverLicenseDetails,
    prefillIssuingCountry,
  });

  const {
    frequentFlyerList,
    onFrequentFlyerSubmit,
    onRemoveFrequentFlyer,
    formFields: frequentFlyerFormFields,
    frequentFlyerDetailsDefaultValues,
  } = useFrequentFlyerFormFields({ traveler });

  const { formFields: additionalInfoFormFields, additionalInfoDefaultValues } =
    useAdditionalInfoFormFields();

  const showDriverInfo =
    travelerEditMode === TravelerEditMode.CARS_ONLY ||
    travelerEditMode === TravelerEditMode.ALL;
  const showFlightSections =
    travelerEditMode === TravelerEditMode.FLIGHTS_ONLY ||
    travelerEditMode === TravelerEditMode.ALL;

  const initialFormValues = getTravellerFormValues({
    traveler,
    dateFormat,
    defaultValues: {
      ...travellerDetailsDefaultValues,
      ...(showPassportDetails ? passportDetailsDefaultValues : {}),
      ...(showDriverInfo ? licenseDefaultValues : {}),
      ...(showFlightSections ? frequentFlyerDetailsDefaultValues : {}),
      ...(showFlightSections ? additionalInfoDefaultValues : {}),
    },
  });

  const formContext = useForm<TravellerFormValues>({
    defaultValues: initialFormValues,
    mode: "onBlur",
  });
  // This is to register the form fields with validations and errors

  for (const [name, details] of Object.entries({
    ...travelerFormFields,
    ...(showPassportDetails ? passportFormFields : {}),
    ...(showDriverInfo ? licenseFields : {}),
    ...(showFlightSections ? frequentFlyerFormFields : {}),
    ...(showFlightSections ? additionalInfoFormFields : {}),
  })) {
    formContext.register(
      name as keyof TravellerFormValues,
      details as FieldValues
    );
  }

  const { trigger, setError } = formContext;
  const firstNameError = initialNameErrors?.firstName;
  const middleNameError = initialNameErrors?.middleName;
  const lastNameError = initialNameErrors?.lastName;
  useEffect(() => {
    for (const [field, message] of Object.entries({
      firstName: firstNameError,
      middleName: middleNameError,
      lastName: lastNameError,
    })) {
      if (message)
        setError(field as "firstName" | "middleName" | "lastName", {
          type: "manual",
          message,
        });
    }
  }, [firstNameError, middleNameError, lastNameError, setError]);
  useEffect(() => {
    if (traveler && enforceMinNameLength) {
      void trigger(["firstName", "lastName"]);
    }
  }, [traveler, enforceMinNameLength, trigger]);

  const onSubmitTraveller = formContext.handleSubmit(() => {
    const newTraveler = convertFormToTraveler({
      traveler: traveler || {},
      formValues: formContext.getValues(),
      frequentFlyerList,
      dateFormat,
      travelerEditMode,
    });

    onSubmit(newTraveler);
  });

  return {
    context: {
      formContext,
      frequentFlyerList,
      existingPassenger: traveler && traveler.id,
    },
    handlers: {
      onSubmitTraveller,
      onFrequentFlyerSubmit,
      onRemoveFrequentFlyer,
    },
  };
};
